import { AfterViewInit, Component, OnInit, ViewChild, inject } from '@angular/core';
import { Router } from '@angular/router';
import { hasIn } from 'lodash';

import { SessionService } from './../../../app/@shared/services/session.service';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Store } from '@ngrx/store';
import { NgrxService } from './../../../app/@shared/services/ngrx.service';
import * as AuthActions from './../../../app/@store/auth/auth.actions';
import * as AuthSelectors from './../../../app/@store/auth/auth.selectors';
import { InlineAlertModule, InlineAlertSeverity } from '@gridscale/ingrid/elements/inline-alert';
import { HttpClient } from '@angular/common/http';
import * as _ from 'lodash';
import { ButtonModule } from '@gridscale/ingrid/elements/button';
import { CommonModule } from '@angular/common';
import { FormControl, FormsModule } from '@angular/forms';
import { PasswordModule } from '@js/primeng17/password';
import { InputTextModule } from '@js/primeng17/inputtext';
import { catchError, filter, of } from 'rxjs';
import { LoaderModule } from '@gridscale/ingrid/components/loader';
import { LayoutHeaderModule } from '@gridscale/ingrid/components/layout-header';
import { LocalstorageService } from '@gridscale/gs-services/localstorage.service';
import { TooltipModule } from '@gridscale/ingrid/elements/tooltip';
import { ConfigService } from '@gridscale/ingrid/helper/services/config.service';
import { environment } from '../../../environments/environment';
import { A11yLinkDirective } from '@gridscale/ingrid/helper/directives';

@Component({
    selector: 'app-login',
    imports: [
        A11yLinkDirective,
        CommonModule,
        ButtonModule,
        FormsModule,
        InlineAlertModule,
        InputTextModule,
        LayoutHeaderModule,
        LoaderModule,
        PasswordModule,
        TooltipModule,
        TranslateModule,
    ],
    templateUrl: './login.component.html',
    styleUrls: ['./login.component.scss']
})
export class LoginComponent implements OnInit, AfterViewInit {
  readonly #configService = inject(ConfigService);
  @ViewChild('emailField') emailField: any;
  @ViewChild('emailNode') emailNode: any;
  @ViewChild('passwordField') passwordField: any;
  @ViewChild('passwordNode') passwordNode: any;
  @ViewChild('otpField') otpField: any;
  loginModel: any = {
    email: '',
    password: '',
    otp: undefined,
    force_login: true
  };
  loginErrorMessage: {
    severity: InlineAlertSeverity;
    summary: string;
    detail: string;
  }[] = [];
  passwordVisible = false;
  currentLanguage = this.translate.currentLang || this.translate.defaultLang;

  isLoggingIn$ = this.store.select(AuthSelectors.isLoggingIn);

  showOTP = false;

  fullSizeLoading = false;

  constructor(
    private sessionService: SessionService,
    private router: Router,
    private translate: TranslateService,
    private store: Store,
    private ngrxService: NgrxService,
    private httpClient: HttpClient,
    private readonly localStorage: LocalstorageService
  ) {
    this.translate.onLangChange.subscribe(_lang => {
      this.currentLanguage = _lang.lang;
    });
  }

  ngOnInit() {
    this.currentLanguage = this.translate.defaultLang;
  }

  ngAfterViewInit() {
    if (this.isTicketValidationLogin) {
      this.emailNode?.nativeElement?.focus();
    }
    setInterval(() => {
      if (this.isTicketValidationLogin) {
        this.emailNode?.nativeElement?.dispatchEvent(new Event('input'));
        this.emailNode?.nativeElement?.dispatchEvent(new Event('change'));
        this.emailNode?.nativeElement?.dispatchEvent(new Event('keydown'));
        this.passwordNode?.nativeElement?.dispatchEvent(new Event('input'));
        this.passwordNode?.nativeElement?.dispatchEvent(new Event('change'));
        this.passwordNode?.nativeElement?.dispatchEvent(new Event('keydown'));
      }
    }, 1050);
  }

  onSubmit() {
    if (!this.emailField.invalid && !this.passwordField.invalid && (!this.otpField || !this.otpField.invalid)) {
      this.loginErrorMessage = [];

      this.ngrxService.callBasicActionWithCallbacks({
        actionToCall: AuthActions.login({ data: { ...this.loginModel } }),
        successAction: AuthActions.loginSuccess,
        failureAction: AuthActions.loginFailure,
        successCallback: _result => {
          const newSession = JSON.parse(String(this.localStorage.getItem('gs_pp_auth', '{}')));

          // get a one-time token to pass to PaPa on new host
          if (window.location.host.match(/^partner\.gridscale\.(io|dev)$/)) {
            // we should redirect to the new partner URL

            const url = this.#configService.partnerLegacyApi + '/api';

            this.fullSizeLoading = true;
            this.httpClient.get(url + '/v1/').subscribe((config: any) => {
              this.httpClient
                .post<{ one_time_token: string }>(url + '/one-time-token-storage', {
                  api_token: newSession.token,
                  partner_uuid: newSession.partner_uuid,
                  project_uuid: newSession.project_uuid,
                  contract_uuid: newSession.contract_uuid,
                  user_uuid: newSession.user_uuid
                }).pipe(
                  catchError(e => {
                    this.fullSizeLoading = false; return of();
                  }),
                  filter(tokenResult => tokenResult?.one_time_token !== undefined),
                ).subscribe(tokenResult => {
                  // logout on this domain
                  this.localStorage.removeItem('gs_pp_auth');

                  let tokenURL = '';

                  // Add Token to Redirect if we are on the Partner host!
                  try {
                    if (this.localStorage.getItem('validateticket') && window.location.host.search('partner.') >= 0) {
                      tokenURL = '&validateticket=' + this.localStorage.getItem('validateticket');
                      this.localStorage.removeItem('validateticket');
                    }
                  } catch (error) { }

                  window.location.href = config.partner.panel_url + '/Partner/?oneTimeToken=' + tokenResult?.one_time_token + tokenURL;
                }
                );
            });
            return;
          }

          window.setTimeout(() => {
            if (this.sessionService.redirectUrl) {
              this.router.navigate(this.sessionService.redirectUrl.split('/'));
              this.sessionService.redirectUrl = undefined;
            } else {
              this.router.navigate(['/Dashboard']);
            }
          }, 10);
        },
        failureCallback: error => {
          // Take Action Error
          const _error = error.error;

          if (hasIn(_error, 'error.body.description') && _error.error.body.description.search('TwoFactorAuthRequiredError') >= 0) {
            this.showOTP = true;
          } else if (hasIn(_error, 'status') && _error.status == 403) {
            this.loginErrorMessage.push({
              severity: InlineAlertSeverity.warning,
              summary: this.translate.instant('login_error_credentials'),
              detail: this.translate.instant('login_error_credentials_detail')
            });
          } else if (hasIn(_error, 'status') && _error.status == 423) {
            this.showOTP = true;
          } else {
            this.loginErrorMessage.push({
              severity: InlineAlertSeverity.error,
              summary: this.translate.instant('unknown_error'),
              detail: this.translate.instant('unknown_error_detail')
            });
          }
        }
      });
    }
  }

  getErrorMessage(fieldName: string) {
    return _.get(this, fieldName) !== undefined
      ? (_.get(this, fieldName) as FormControl)?.hasError('required')
        ? this.translate.instant('input_invalid_required')
        : (_.get(this, fieldName) as FormControl)?.hasError('email')
          ? this.translate.instant('input_invalid_email')
          : (_.get(this, fieldName) as FormControl)?.hasError('minlength')
            ? this.translate.instant('input_invalid_minlength', { minlength: 8 })
            : ''
      : '';
  }

  otpchange(_e: string) {
    if (_e && _e.length == 6) {
      setTimeout(() => {
        this.onSubmit();
      }, 10);
    }
  }

  selectLanguage(lang: string) {
    this.translate.use(lang);
  }

  get isTicketValidationLogin(): boolean {
    return (this.localStorage.getItem('validateticket') !== null && this.localStorage.getItem('validateticket') !== '')
  }

  get isProduction() {
    return environment.production;
  }
}
